import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Voice from "./components/Voice";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import ChatThreadInput from "./components/ChatThreadInput";
import iconAi from "url:shared-assets/icons/ai.svg";
import iconAIThinking from "url:shared-assets/animations/sparkles_animated_infinite.svg"
import iconAIDone from "url:shared-assets/animations/sparkles_animated_ending.svg";
import iconWarning from "url:shared-assets/icons/warning.svg";
import iconDocument from "url:shared-assets/icons/white/document.svg";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import CustomButton from "../../components/CustomButton";
import { MessageInterface, UploadFile } from "./interfaces";
import {
  loadAiStart,
  AiThreadStart,
  getPrompts,
} from "../../helpers/ai_thread";
import { ThreadInterface } from "../../interfaces/common";
import { threadsAtom, uploadFileAtom } from "../../jotai_state";
import iconArrowRight from "url:shared-assets/icons/white/arrow-right.svg";
import { zDateFull } from "../../helpers/dateFunctions";

interface DebugInfo {
  aiSystem: string;
  prompt: string;
  responseTime: number;
  tokensIn: number;
  tokensOut: number;
  useWebSearch: boolean;
}

enum StreamEnum {
  WAITING,
  THINKING,
  DONE
}

enum LastAction {
  NONE,
  CHANGE_THREAD,
  SENT_QUESTION,
  RECEIVED_ANSWER,
}

const CHAT_PAGE = 10; // NOTE:number of messages to load per API call

const AiStart: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [threads, setThreads] = useAtom(threadsAtom);
  const [fileRaw, setFileRaw] = useAtom(uploadFileAtom);
  const [focusAreaId, setFocusAreaId] = useState<string>("");
  const [threadId, setThreadId] = useState<string>("0");
  const [coachStyle, setCoachStyle] = useState<string>("assistant");
  const [useVoice, setUseVoice] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [noScroll, setNoScroll] = useState(false);
  const [messages, setMessages] = useState<Array<MessageInterface>>([]);
  const [lastMessage, setLastMessage] = useState("");
  const [message, setMessage] = useState<MessageInterface | null>(null);
  const [ephemeralKey, setEphemeralKey] = useState<string>("");
  const [streamStatus, setStreamStatus] = useState<StreamEnum>(StreamEnum.WAITING);
  const [emmaThinking, setEmmaThinking] = useState<boolean>(false);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [newQuestion, setNewQuestion] = useState(false);
  const [lastAction, setLastAction] = useState<LastAction>(LastAction.NONE);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  // load old message
  const [loadingMoreMsgs, setLoadingMoreMsgs] = useState(false);
  // Stop calling API when react very first message
  const [allMsgsLoaded,   setAllMsgsLoaded]   = useState(true);
  // Stable refs — values readable inside scroll listener and loadMoreMsgs without going stale
  const allMsgsLoadedRef    = useRef(true);
  const loadingMoreMsgsRef  = useRef(false);
  const threadIdRef         = useRef<string>("0");
  const firstMsgNumberRef   = useRef<number | null>(null);

  // Scroll restoration ref — set just before setMessages, consumed by useLayoutEffect
  const pendingScrollRestoreRef = useRef<{
    prevScrollTop: number;
    prevScrollHeight: number;
  } | null>(null);

  const now = new Date();

  // Scroll restoration 
  useLayoutEffect(() => {
    const restore = pendingScrollRestoreRef.current;
    if (restore && chatWindowRef.current) {
      const added = chatWindowRef.current.scrollHeight - restore.prevScrollHeight;
      chatWindowRef.current.scrollTop = restore.prevScrollTop + added;
      pendingScrollRestoreRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    if (location.pathname.startsWith("/ai/")) {
      const _threadId = location.pathname.substring(4);
      if (_threadId !== "0") {
        setNoScroll(false);
        setLastAction(LastAction.CHANGE_THREAD);
        selectThread(_threadId);
      } else {
        setLastAction(LastAction.NONE);
        setThreadId("0");
        threadIdRef.current = "0";
        setMessages([]);
        setLoaded(true);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (streamStatus === StreamEnum.DONE) {
      setLastAction(LastAction.RECEIVED_ANSWER);
      if (message) {
        addMessage(message);
        setMessage(null);
      }
      setLastMessage("");
    }
  }, [streamStatus]);

  useEffect(() => {
    if (loaded) {
      const aiThreadStart = loadAiStart();

      if (aiThreadStart) {
        if (aiThreadStart.aiStyle) {
          setCoachStyle(aiThreadStart.aiStyle);
        }
        if (aiThreadStart.focusAreaId) {
          setFocusAreaId(aiThreadStart.focusAreaId);
        }
        let message = aiThreadStart.message;
        if (message === null) {
          if (aiThreadStart.type === "focusArea") {
            message =
              lang === "sv"
                ? "Jag vill starta coaching inom fokusområdet"
                : "I want to start coaching within the focus area";
          } else if (aiThreadStart.type === "assistant") {
            setCoachStyle("assistant");
            message =
              lang === "sv"
                ? "Lär mig mer om ämnet"
                : "Learn more about the topic";
          } else if (aiThreadStart.type === "voice") {
            startVoice();
          }
        }
        if (message || fileRaw) {
          const _message = message ? message : ""
          send(_message, aiThreadStart, fileRaw);
        } else if (aiThreadStart.systemMessage) {
          setMessages([
            {
              role: "system",
              message: aiThreadStart.systemMessage,
              sent: new Date(),
              aiFile: [],
            }
          ])
        }
      }
    }
  }, [loaded]);

  useEffect(() => {
    if (threadId === "_old_chat") {
      if (chatWindowRef.current) {
        chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
      }
    } else {
      if (messages.length > 1) {
        nameThread();
      }
      // Keep the scroll listener blocked until the initial scroll completes:
      if (lastAction === LastAction.CHANGE_THREAD) {
        loadingMoreMsgsRef.current = true;   // block scroll listener
        setLastAction(LastAction.NONE);
        setTimeout(() => {
          const lastMsg = document.getElementById("last_msg_true");
          const container = chatWindowRef.current;
          if (lastMsg && container) {
            const msgTop = lastMsg.getBoundingClientRect().top
                        - container.getBoundingClientRect().top
                        + container.scrollTop;
            container.scrollTo({ top: msgTop });
          }
          loadingMoreMsgsRef.current = false;  // unblock after scroll
        }, 250);
      }else if (lastAction === LastAction.SENT_QUESTION) {
        scrollToTopOfQuestion();
      }
    }
  }, [messages]);

  // Scroll listener
  useEffect(() => {
    const container = chatWindowRef.current;
    if (!container) return;
    function onScroll() {
      if (container!.scrollTop < 80) loadMoreMsgs();
    }
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [loaded, threadId]);

  const loadMoreMsgs = useRef(() => {
    if (loadingMoreMsgsRef.current || allMsgsLoadedRef.current) return;
    const _threadId = threadIdRef.current;
    if (!_threadId || _threadId === "0" || _threadId === "_old_chat") return;

    loadingMoreMsgsRef.current = true;
    setLoadingMoreMsgs(true);
    const container = chatWindowRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    const prevScrollTop    = container?.scrollTop    ?? 0;

    const qs = firstMsgNumberRef.current != null
      ? `?limit=${CHAT_PAGE}&before_number=${firstMsgNumberRef.current}`
      : `?limit=${CHAT_PAGE}`;

    api.get(`client/chat_thread/${_threadId}${qs}`).then((response) => {
      const older: MessageInterface[] = (response.json.messages as Array<any>).map((msg: any) => ({
        number:       msg.number,
        role:         msg.role,
        message:      msg.message,
        sent:         new Date(msg.sent),
        functionCall: msg.functionCall,
        taskId:       msg.taskId,
        aiFile:       Array.isArray(msg.aiFile) ? msg.aiFile : (msg.aiFile ? [msg.aiFile] : []),
      }));

      if (older.length === 0) {
        allMsgsLoadedRef.current = true;
        setAllMsgsLoaded(true);
        loadingMoreMsgsRef.current =  false;
        return;
      }
      if (older[0].number === 0) {
        allMsgsLoadedRef.current = true;
        setAllMsgsLoaded(true);
      }
      firstMsgNumberRef.current = older[0].number ?? null;
      pendingScrollRestoreRef.current = { prevScrollTop, prevScrollHeight };
      setMessages(prev => [...older, ...prev]);
    }).finally(() => {
      setLoadingMoreMsgs(false);
    });
  }).current;

  useEffect(() => {
    if (!loadingMoreMsgs) {
      loadingMoreMsgsRef.current = false;
    }
  }, [loadingMoreMsgs]);


  const scrollToTopOfQuestion = () => {
    setTimeout(() => {
      const lastMsg = document.getElementById("last_msg_true");
      const container = chatWindowRef.current;
      // getBoundingClientRect() which always returns the actual rendered position
      if (lastMsg && container) {
      const msgTop = lastMsg.getBoundingClientRect().top
                   - container.getBoundingClientRect().top
                   + container.scrollTop;
         container.scrollTo({
          top: msgTop,
          left: 0,
          behavior: 'smooth',
        });
      }
    }, 200);
  };

  const selectThread = (threadId: string) => {
    threadIdRef.current        = threadId;
    firstMsgNumberRef.current  = null;
    allMsgsLoadedRef.current   = false;
    setAllMsgsLoaded(false);
    loadingMoreMsgsRef.current = true;

    api.get(`client/chat_thread/${threadId}?limit=${CHAT_PAGE}`).then((response) => {
      if (response.json.error) {
        alert(response.json.error);
        setError(response.json.error);
      }
      setNewQuestion(false);

      const _messages: Array<MessageInterface> = [];
      for (const msg of response.json.messages as Array<MessageInterface>) {
        _messages.push({
          number: msg.number,
          role: msg.role,
          message: msg.message,
          sent: new Date(msg.sent),
          functionCall: msg.functionCall,
          taskId: msg.taskId,
          aiFile: Array.isArray(msg.aiFile) ? msg.aiFile : (msg.aiFile ? [msg.aiFile] : []),
        });
      }

      // Determine if we are already at the beginning of the thread
      if (_messages.length > 0 && _messages[0].number != null) {
        firstMsgNumberRef.current = _messages[0].number;
        if (_messages[0].number === 0) {
          allMsgsLoadedRef.current = true;
          setAllMsgsLoaded(true);
        }
      } else {
        // No messages or no number — nothing more to load
        allMsgsLoadedRef.current = true;
        setAllMsgsLoaded(true);
      }

      setMessages(_messages);
      setFocusAreaId(response.json.focusAreaId);
      if (response.json.coachStyle) {
        setCoachStyle(response.json.coachStyle);
      } else if (response.json.focusAreaId) {
        setCoachStyle("coaching");
      } else {
        setCoachStyle("assistant");
      }
      const _threads = [...threads];
      for (const _thread of _threads) {
        if (_thread.id === threadId) {
          _thread.selected = true;
        }
      }
      setThreads(_threads);
      setThreadId(threadId);
      threadIdRef.current = threadId;
      setLoaded(true);
      loadingMoreMsgsRef.current = false; // unblock scroll listener
    });
  };

  const clickTopic = (topic: string) => {
    api
      .get("client/topic_click?f=ai_tip&t=" + topic)
      .then(() => {
        send(topic, null, [])
      });
  }

  const addThread = (_threadId: string, _name: string | null) => {
    const now = new Date().toISOString();
    const newThread: ThreadInterface = {
      id: _threadId,
      topic: "New topic",
      name: _name ? _name : lang === "sv" ? "Eget ämne" : "Own topic",
      created: now,
      updated: now,
      focusAreaId: focusAreaId,
      selected: true,
    };
    setThreads([newThread, ...threads]);
  };

  const addMessage = (msg: MessageInterface) => {
    setMessages((prevMessages) => [...prevMessages, msg]);
  };

  const saveMessage = (msg: MessageInterface) => {
    setMessages((prevMessages) => [...prevMessages, msg]);
    const data = {
      role: msg.role,
      message: msg.message,
      sent: msg.sent,
      functionCall: msg.functionCall,
      threadId: threadId,
      messageType: useVoice ? 2 : 1,
    };
    api.post("client/chat_thread_save", data).then();
  };

  const getThread = () => {
    for (const thread of threads) {
      if (thread.id === threadId) {
        return thread;
      }
    }
    return null;
  };

  const nameThread = () => {
    const thread: ThreadInterface | null = getThread();

    if (thread && thread.topic === "New topic") {
      if (messages.length < 6) {
      // Try naming the thread early if we have enough data
        let message_length = 0;
        for (let m of messages) {
          message_length += m.message.length;
        }
        if (message_length < 300) {
          return;
        }
      }
      api.get("client/chat_thread_give_name/" + threadId).then((result) => {
        const threadName: string = result.json.name;
        const _threads: Array<ThreadInterface> = [...threads];
        for (const _thread of _threads) {
          if (_thread.id === threadId) {
            _thread.topic = threadName;
            break;
          }
        }
        setThreads(_threads);
      });
    }
  };

  async function handleStream(reader: ReadableStreamDefaultReader) {
    let done, value;
    let streamResponse = "";
    let finalResponse = "";
    const decoder = new TextDecoder();

    while (!done) {
      ({ value, done } = await reader.read());

      if (done) {
        console.log("Closed stream. finalResponse is " + finalResponse.length + " bytes");
        if (finalResponse.length > 0) {
          try {
            // console.log(finalResponse);
            const data = JSON.parse(finalResponse);
            if (data.aiResponse) {
              setMessage({
                role: "assistant",
                message: data.aiResponse,
                sent: new Date(),
                functionCall: data.functionCall,
                taskId: data.taskId,
                aiFile: []
              });
              setDebugInfo(data.debug);
            } else {
              console.log(data)
            }
            if (threadId === "0" && data.threadId) {
              addThread(data.threadId, null);
              navigate("/ai/" + data.threadId);
            }
          } catch (error) {
            console.log(error);
          }
        }
        return;
      }

      const responseData = decoder.decode(value);
      
      // console.log("Got data: " + responseData);

      // If we have received start of finalResponse, add to it
      if (finalResponse.length > 0) {
        console.log('Adding ' + responseData.length + " bytes to finalResponse which is " + finalResponse.length);
        finalResponse += responseData;
      } else {
        // Look for position of {"aiResponse". If we find that, this is the start of finalResponse
        const aiResponse = responseData.indexOf('{"aiResponse"');
        if (aiResponse > -1) {
          console.log("Found start of final message at " + aiResponse);
          finalResponse = responseData.substring(aiResponse);
        }
      }

      if (finalResponse.length === 0) {
        if (responseData && responseData.startsWith("_-_")) {
          setLastMessage(responseData.substring(3));
        } else {
          streamResponse += responseData;
          setLastMessage(streamResponse);
        }
      }
    }
  }

  const send = (text: string, aiThreadStart: AiThreadStart|null,
                aiFiles: UploadFile[]=[]) => {
    setNoScroll(true);
    setNewQuestion(true);
    setLastAction(LastAction.SENT_QUESTION);
    const msg: MessageInterface = {
      role: "user",
      message: text,
      sent: new Date(),
      aiFile: aiFiles
    };
    addMessage(msg);

    let _focusAreaId = aiThreadStart ? aiThreadStart.focusAreaId : focusAreaId;
    let _threadId = aiThreadStart ? aiThreadStart.threadId : threadId;
    let _taskId = aiThreadStart ? aiThreadStart.taskId : null;
    let _coachStyle = coachStyle;
    let _originId = aiThreadStart ? aiThreadStart.originId : null;
    let _originName = aiThreadStart ? aiThreadStart.originName : null;
    if (aiThreadStart && aiThreadStart.aiStyle)
      _coachStyle = aiThreadStart.aiStyle;

    if (!_threadId) {
      _threadId = "0";
    }

    const data = {
      focusAreaId: _focusAreaId,
      threadId: _threadId,
      coachStyle: _coachStyle,
      taskId: _taskId,
      message: text,
      messageType: useVoice ? 2 : 1,
      originId: _originId,
      originName: _originName,
      aiFile: aiFiles,
    };

    setFileRaw([]);

    const url = "fast_api/client/chat_thread_send_thread_stream";

    setStreamStatus(StreamEnum.THINKING);
    setLastMessage("&nbsp;");

    api.post(url, data, [], false, true)
      .then(response => {
        if (response.response.ok) {
          if (response.response.body) {
            handleStream(response.response.body.getReader()).then(() => {
              setStreamStatus(StreamEnum.DONE);
              setTimeout(() => {
                // Wait before enabling scroll again, since other processes want to scroll
                setNoScroll(false);
              }, 800)
            });
          }
        } else {
          alert(response.response.statusText);
        }
      })
      .catch(error =>  {
        // This should be displayed as alert from api handler
        console.log(error);
        setStreamStatus(StreamEnum.DONE);
        setEmmaThinking(false);
      });
  }

  // Correct scroll
  const chatInputHeightChange = (height: number) => {
    if (noScroll) {
      // Do not scroll if explicitly disabled
      return;
    }
    const container = chatWindowRef.current;
    if (!container) return;
    const winWidth = window.innerWidth;
    const ySpace = winWidth < 636 ? 105 : 121;
    const subtractHeight = ySpace + height;
    // Always resize the container.
    container.style.height = "calc(100dvh - " + subtractHeight + "px)";

    // Only scroll to bottom if the user is already near the bottom.
    // When the user has scrolled up to load older messages the distance
    // from the bottom is large, so we leave the scroll position alone.
    // This replaces all timing-based ref/lastAction guards which were
    // unreliable because ResizeObserver callbacks fire asynchronously
    // outside the React commit cycle.
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 100) {
      container.scrollTo(0, container.scrollHeight);
    }
  };

  const startVoiceSession = (_threadId: string) => {
    const data = {
      threadId: _threadId,
      coachStyle: coachStyle,
      language: lang,
    };
    api.post("client/voice_session", data).then((response) => {
      if (response.json.status === "error") {
        alert(response.json.error);
      } else {
        if (response.json.value) {
          setEphemeralKey(response.json.value);
          setUseVoice(true);
        } else {
          alert("Unknown error: " + response.json.error);
        }
      }
    });
  };

  const startVoice = () => {
    // const showError = CONFIG.SYSTEM === "production" ? true : false;
    // if (showError) {
    //   alert("Voice is temporarily out of service - we are troubleshooting the Azure voice system");
    //   return;
    // }
    if (threadId && threadId !== "0") {
      startVoiceSession(threadId);
    } else {
      const data = {
        focusAreaId: focusAreaId,
        coachStyle: coachStyle,
        language: lang,
      };

      api.post("client/chat_thread_start", data).then((response) => {
        if (response.json.status === "ok") {
          addThread(response.json.threadId, response.json.threadSubject);
          setThreadId(response.json.threadId);
          threadIdRef.current = response.json.threadId;
          history.pushState(null, "", "/ai/" + response.json.threadId);
          startVoiceSession(response.json.threadId);
        } else {
          alert("Error: " + response.json.error);
        }
      });
    }
  };

  const stopVoice = () => {
    setUseVoice(false);
  };

  const renderInput = () => {
    if (threadId === "_old_chat") {
      return (
        <div className="mt-4 border border-cherry-500 rounded p-4 text-gray-400">
          {lang === "sv"
            ? "Du kan inte chatta med AI-Coachen i den här tråden längre - endast läsa."
            : "You can't chat with the AI Coach in this thread any more - only read"}
        </div>
      );
    }
    let placeholder: string | undefined = undefined;
    if (threadId === "0") {
      if (coachStyle === "assistant") {
        placeholder =
          lang === "sv" ? "Fråga vad som helst..." : "Ask anything...";
      } else {
        placeholder =
          lang === "sv"
            ? "Fråga vad som helst för att starta en coachsession"
            : "Ask anything to start a coaching session...";
      }
    }
    return (
      <div className="z-chat-bottom ml-0 md:ml-16">
        {
          debugInfo &&
          <div style={{position: "absolute", bottom: 12, right: 12}} onClick={() => setShowDebug(!showDebug)}>
            <img src={iconWarning} className="h-6 w-6 cursor-pointer" alt="debug"/>
          </div>
        }
        {
          showDebug &&
            <div className="bg-indigo-800 text-xs text-gray-400 overflow-y-scroll"
                 style={{
                   position: "fixed",
                   top: 80,
                   right: 0,
                   width: 300,
                   height: "calc(100dvh - 220px)",
                   padding: 4,
                   borderLeft: "1px solid #202048"
                 }}>
                <p>AI System: {debugInfo?.aiSystem}</p>
                <p>Response time: {debugInfo?.responseTime.toFixed(2)}s</p>
                <p>Tokens sent: {debugInfo?.tokensIn}</p>
                <p>Tokens received: {debugInfo?.tokensOut}</p>
                <p>Web search: {debugInfo?.useWebSearch ? "Yes" : "No"}</p>
                <p className="mt-4">Prompt:</p>
                <div className="whitespace-pre-line">{debugInfo?.prompt}</div>
            </div>
        }
        <div className="z-chat-bottom-box bg-indigo-900 relative">
          <div className="z-gradient-crop"/>
          <ChatThreadInput
            sendToOtherPage={false}
            handleSendMessage={send}
            onHeightChange={chatInputHeightChange}
            setCoachStyle={setCoachStyle}
            coachStyle={coachStyle}
            startVoice={startVoice}
            emmaThinking={emmaThinking || streamStatus === StreamEnum.THINKING}
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  };

  const renderFile = (aiFiles: UploadFile[]) => {
    if (!aiFiles || aiFiles.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {aiFiles.map((aiFile, idx) => {
          if (aiFile.filetype === "image") {
            return (
              <div key={idx} className="mt-4 align-right ml-auto max-w-[67%]">
                <img src={aiFile.bytes} className="rounded-md" alt="img" style={{maxHeight: "30dvh"}}/>
              </div>
            );
          } else if (aiFile.filetype === "pdf") {
            return (
              <div key={idx} className="mt-4 flex align-right ml-auto rounded-md bg-indigo-900 p-2 items-center">
                <div className="rounded-sm bg-indigo-700 p-2">
                  <img src={iconDocument} alt="pdf" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-gray-400 z-title4">
                  <div className="truncate" style={{width: 150}}>{aiFile.filename}</div>
                  <p>PDF</p>
                </div>
              </div>
            );
          } else if (aiFile.filetype === "excel") {
            return (
              <div key={idx} className="mt-4 flex align-right ml-auto rounded-md bg-indigo-900 p-2 items-center">
                <div className="rounded-sm bg-indigo-700 p-2">
                  <img src={iconDocument} alt="excel" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-gray-400 z-title4">
                  <div className="truncate" style={{width: 150}}>{aiFile.filename}</div>
                  <p>Excel</p>
                </div>
              </div>
            
            );
          } else if (aiFile.filetype === "word") {
            return (
              <div key={idx} className="mt-4 flex align-right ml-auto rounded-md bg-indigo-900 p-2 items-center">
                <div className="rounded-sm bg-indigo-700 p-2">
                  <img src={iconDocument} alt="word" className="h-6 w-6" />
                </div>
                <div className="ml-2 text-gray-400 z-title4">
                  <div className="truncate" style={{width: 150}}>{aiFile.filename}</div>
                  <p>Word</p>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderMessage = (message: MessageInterface) => {
    if (message.functionCall) {
      const name = message.functionCall.name;
      let args: any = message.functionCall.arguments;
      if (typeof args === "string") {
        try {
          args = JSON.parse(message.functionCall.arguments);
        } catch (error) {
          console.log(message.functionCall.arguments);
          return <div>Error: thread is broken ({message.message})</div>;
        }
      }

      // const args = message.functionCall.arguments
      if (name === "create_task") {
        const taskName = args.name;
        let url = message.taskId ? "/tasks/" + message.taskId : "/tasks";
        return (
          <div className="mt-2 py-4 text-center">
            <CustomButton
              buttonType="outlineWhiteBase"
              onClick={() => navigate(url)}
            >
              {lang === "sv" ? "Gå till aktivitet" : "Go to Action"} "{taskName}
              "
            </CustomButton>
          </div>
        );
      } else if (name === "create_activity") {
        const taskName = args.name;
        let url = "/dev-plan"
        if (message.functionCall.parentId && message.functionCall.id) {
          url += "/long-term-goal/" + message.functionCall.parentId + "#activity_" + message.functionCall.id;
        }
        return (
          <div className="mt-2 py-4 text-center">
            <CustomButton
              buttonType="outlineWhiteBase"
              onClick={() => navigate(url)}
            >
              {lang === "sv" ? "Gå till aktivitet" : "Go to Activity"} "{taskName}
              "
            </CustomButton>
          </div>
        );
      } else if (name === "edit_task") {
        const taskName = args.name;
        const taskId = args.id;
        return (
          <div className="mt-2 py-4 text-center">
            <CustomButton
              buttonType="outlineWhiteBase"
              onClick={() => navigate("/tasks/" + taskId)}
            >
              {lang === "sv" ? "Uppdaterade aktivitet" : "Updated Action"} "
              {taskName}"
            </CustomButton>
          </div>
        );
      } else if (name === "name_thread") {
        return (
          <div className="py-4">
            {lang === "sv" ? "Döpte tråden till" : "Renamed thread to "} "
            {args.summary}"
          </div>
        );
      }
      return <div>Error: no handler for task {message.functionCall.name}</div>;
    } else if (message.message) {
      return (
        <div className="markdown-content text-gray-300">
          <Markdown remarkPlugins={[remarkGfm]}>
            {message.message}
          </Markdown>
        </div>
      );
    } else if (Array.isArray(message.aiFile) && message.aiFile.length > 0) {
      return <div className="py-4">{lang === "sv" ? "Uppladdad fil" : "Uploaded file"}</div>;
    } else {
      return <div className="py-4">Error: empty message</div>;
    }
  };

  const maybeDateDivider = (messageNr: number) => {
    let d: Date | null = null;
    if (messageNr === 0) {
      d = messages[0].sent;
    } else {
      const dPrev = zDateFull(messages[messageNr - 1].sent, lang, true);
      const dThis = zDateFull(messages[messageNr].sent, lang, true);
      if (dPrev !== dThis) {
        d = messages[messageNr].sent;
      }
    }
    if (d) {
      const showYear = d.getFullYear() !== now.getFullYear();
      return (
        <div className="flex items-center mt-4">
          <hr className="border-indigo-800 grow" />
          <div className="px-2 border border-indigo-800 rounded-xl text-xs text-gray-400">
            {zDateFull(d, lang, showYear)}
          </div>
          <hr className="border-indigo-800 grow" />
        </div>
      );
    }
  };

  const getLastIndex = (role: string) => {
    for (let i = messages.length -1; i >= 0; i--) {
      if (messages[i].role === role) return i
    }
    return -1;
  }

  const renderThread = () => {

    const lastUser = getLastIndex("user");
    const lastAssistant = getLastIndex("assistant");

    return (
      <div className="mt-4 mx-auto relative" style={{ maxWidth: 760 }}>

        {loadingMoreMsgs && (
          <div className="flex justify-center py-2">
            <img
              src={animationProcessing}
              alt="loading"
              style={{ height: 16, width: 114 }}
            />
          </div>
        )}

        <div
          ref={chatWindowRef}
          className="overflow-y-scroll z-no-scrollbar relative"
          style={{
            height: "calc(100dvh - 231px)",
            overflowAnchor: "none",
          }}
        >
          {allMsgsLoaded && messages.length > 20 && (
            <div className="flex items-center my-4">
              <hr className="border-indigo-800 grow" />
              <div className="px-2 border border-indigo-800 rounded-xl text-xs text-gray-400">
                {lang === "sv" ? "Början av konversationen" : "Beginning of conversation"}
              </div>
              <hr className="border-indigo-800 grow" />
            </div>
          )}

          {messages.map((message: MessageInterface, index) => {
            let cls = "mt-4";
            if (message.role === "user") {
              cls += " px-4 bg-indigo-800 rounded-[6px] align-right ml-auto max-w-[67%]";
            } else {
              cls += " ml-7"
            }
            return (
              <div
                key={"chat_" + index}
                id={"last_msg_" + (index === lastUser ? "true" : "false")}
                className={"flex flex-col relative" + ((index === lastAssistant && newQuestion && streamStatus !== StreamEnum.THINKING) ? " z-last-msg" : "")}
              >
                {maybeDateDivider(index)}
                {
                  Array.isArray(message.aiFile) && message.aiFile.length > 0 &&
                  renderFile(message.aiFile)
                }
                {
                  (message.role !== "user" && !message.functionCall) &&
                    <div style={{position: "absolute", top: 32, left: 0}}>
                        <img src={iconAIDone} alt="ai" 
                        className="w-6"/>
                    </div>
                }
                <div className={cls}>{renderMessage(message)}</div>
              </div>
            );
          })}

          {lastMessage.length > 0 && (
            <div id="last_msg" className="flex flex-col relative z-last-msg">
              <div style={{position: "absolute", top: 32, left: 0}}>
                <img src={iconAIThinking} alt="ai" className="w-6"/>
              </div>
              <div className="mt-4 ml-7">
                <div className="markdown-content text-gray-300">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {lastMessage}
                  </Markdown>
                </div>
              </div>
            </div>
          )}

          {emmaThinking && (
            <div className="mt-4 flex items-center">
              <img className="h-6 w-6 rounded-full" src={iconAi} alt="pic"/>
              <img
                src={animationProcessing}
                alt="processing"
                className="ml-4"
                style={{height: 16, width: 114}}
              />
            </div>
          )}

          {
            (messages.length > 2 && messages[messages.length - 1].number > 120) &&
            <div className="mt-4 text-gray-300 border border-cherry-600 rounded-md p-4">
                This conversation has reached its limit and capacity.
                No worries – just start a new thread and you’ll be able to continue.
            </div>
          }
          <div className="h-5">&nbsp;</div>
        </div>
      </div>
    );
  };

  if (error.length > 0) {
    return <div className="mt-4">{error}</div>;
  }

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }

  const renderEmptyStart = () => {
    const topics = getPrompts(4, lang);
    return (
      <div
        className="main-one-col.small"
      >
        <div>
          <img src={iconAi} alt="ai" className="h-8 w-8 mx-auto mt-6"/>
          {
            !emmaThinking &&
              <div className="mt-6 z-heading1 text-center">
                {lang === "sv" ? "Hur kan jag hjälpa dig?" : "How can I help you?"}
              </div>
          }

          <div className="flex flex-wrap gap-4 mt-6 justify-center">
            {emmaThinking ? (
              <div className="mt-4 flex items-center">
                <img className="h-6 w-6 rounded-full" src={iconAi} alt="pic" />
                <img
                  src={animationProcessing}
                  alt="processing"
                  className="ml-4"
                  style={{height: 16, width: 114}}
                />
              </div>
            ) : (
              <>
                {topics.map((topic, index) => (
                  <div
                    key={"topic_" + index}
                    onClick={() => clickTopic(topic)}
                    className="main-half-width bg-indigo-800 hover:bg-indigo-700 rounded-md p-3 flex justify-between cursor-pointer"
                  >
                    <p>{topic}</p>
                    <img
                      src={iconArrowRight}
                      alt="right"
                      className="h-4 w-4 mt-6"
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id="chat_space" className="main-one-col small">
      {useVoice && (
        <Voice
          close={stopVoice}
          ephemeralKey={ephemeralKey}
          saveMessage={saveMessage}
          language={lang}
        />
      )}
      {(threadId === "0" && messages.length === 0) ? renderEmptyStart() : renderThread()}
      {renderInput()}
    </div>
  );
};

export default AiStart;
